/**
 * The Transition Notice form's date rules (Site Host Terms Section 13),
 * mirroring the API (apps/api/src/lib/siteHostTransition.ts), which stays
 * the authority. Pure, so they are unit-tested without rendering.
 */
export type TransitionBasis = 'notice' | 'acceptance' | 'amendment';

export const TRANSITION_NOTICE_MIN_DAYS = 30;
export const TRANSITION_NOTICE_MAX_DAYS = 366;
export const TRANSITION_EARLIEST_DAY = '2026-10-02';

export const TRANSITION_BASIS_LABELS: Record<TransitionBasis, string> = {
  notice: 'Date stated in the notice (13.2(c))',
  acceptance: 'Site Host accepted the terms (13.2(a))',
  amendment: 'Signed amendment (13.2(b))',
};
export const TRANSITION_METHOD_LABELS = {
  email: 'Email', courier: 'Courier with tracking', registered_mail: 'Registered or certified mail',
  hand_delivery: 'Hand delivery', other: 'Other',
} as const;

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function defaultTransitionDate(sentOn: string): string {
  return addDaysYmd(sentOn, TRANSITION_NOTICE_MIN_DAYS);
}

export function transitionNoticeError(input: {
  basis: TransitionBasis; sent_on: string; transition_on: string; note: string;
}, today: string): string | null {
  const { basis, sent_on: sent, transition_on: date } = input;
  if (!sent) return 'Enter the date the notice was sent.';
  if (sent < TRANSITION_EARLIEST_DAY) return 'The date sent cannot be before 2 October 2026, when the Site Host Terms were first posted.';
  if (sent > today) return 'The date sent cannot be in the future.';
  if (!date) return 'Enter the Transition Date.';
  if (basis === 'notice') {
    if (date < defaultTransitionDate(sent)) return 'The Transition Date must be at least 30 days after the notice is sent (Site Host Terms 13.2(c)).';
    if (date > addDaysYmd(sent, TRANSITION_NOTICE_MAX_DAYS)) return 'The Transition Date must be within a year of the notice.';
  } else {
    if (date < sent) return 'The Transition Date cannot be before the notice was sent.';
    if (date > today) return 'An acceptance or amendment date cannot be in the future.';
  }
  if (!input.note.trim()) return 'Add a note: what was sent, to whom and where.';
  return null;
}
